import { useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userNeedsJobBoardProfileCompletion } from "../../utils/jobboard/jobboardProfileGate.ts";
import { X, Briefcase, MapPin, DollarSign, ExternalLink, Clock } from "lucide-react";
import ApplyExternalJobOffer from "./ApplyExternalJobOffer.tsx";
import ModalOverlay from "../../components/UI/ModalOverlay/ModalOverlay.tsx";
import JobboardRegistrationFlow, {
  JobboardRegistrationStep,
} from "../../components/WorkBag/JobboardRegistrationFlow.tsx";
import { fireAnalytics } from "../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import { formatSalary, formatDate } from "../../components/MercadoDeTrabajo/WorkOfferDetailsModal.tsx";
import styles from "./WorkApplyModal.module.scss";

interface WorkOfferUser {
  username?: string;
  type?: string;
  razon_social?: string;
  name?: string;
  last_name?: string;
}

interface WorkOfferSkill {
  skill?: { name: string };
}

interface WorkOfferZone {
  jurisdiccion?: { name: string };
  city?: string;
}

export interface WorkOffer {
  id?: string | number;
  user?: WorkOfferUser & { email?: string };
  skill?: WorkOfferSkill[];
  workZones?: WorkOfferZone[];
  salary?: string;
  salaryType?: string;
  activeUntil?: string;
  alternativeContact?: string;
  description?: string;
}

interface WorkApplyModalProps {
  setShowApplyModal: (show: boolean) => void;
  workOffer?: WorkOffer | null;
  forbiddenClose?: boolean;
  setShowForm?: () => void;
  userData?: {
    id?: string | number;
    rols?: { name?: string }[];
    type?: string | null;
  };
  skipWorkBagUserCheck?: boolean;
  prefilledEmail?: string;
  identityValidated?: boolean;
}

const formatSalaryDisplay = (salary?: string, type?: string) => {
  if (!salary || salary === "0.00" || Number.parseFloat(salary) === 0) return "A convenir";
  return formatSalary(salary, type);
};

const WorkApplyModal: React.FC<WorkApplyModalProps> = ({
  setShowApplyModal,
  workOffer,
  forbiddenClose = false,
  setShowForm,
  userData,
}) => {
  const [showApplyExternalJobOffer, setShowApplyExternalJobOffer] = useState(false);
  const [registrationStep, setRegistrationStep] = useState<JobboardRegistrationStep>("email");
  const navigate = useNavigate();

  const isExternalOffer =
    !workOffer || (workOffer?.user?.username?.toLowerCase() ?? "") === "gonzalo@edify.la";

  const companyLabel = workOffer?.user
    ? workOffer.user.type === "COMPANY"
      ? workOffer.user.razon_social ?? "Empresa"
      : [workOffer.user.name, workOffer.user.last_name].filter(Boolean).join(" ").trim() || "Publicador"
    : "Oferta";

  const primaryZone =
    workOffer?.workZones?.[0]?.jurisdiccion?.name ?? workOffer?.workZones?.[0]?.city ?? null;
  const skillNames = (workOffer?.skill ?? []).map((s) => s?.skill?.name).filter(Boolean) as string[];
  const salaryLabel = formatSalaryDisplay(workOffer?.salary, workOffer?.salaryType);
  const activeUntilLabel = workOffer?.activeUntil ? formatDate(workOffer.activeUntil) : null;
  const hasOfferContext = Boolean(
    workOffer &&
      ((!isExternalOffer && companyLabel) ||
        skillNames.length > 0 ||
        primaryZone ||
        salaryLabel !== "A convenir" ||
        activeUntilLabel)
  );

  const handleRegistrationSuccess = () => {
    fireAnalytics("apply_to_offer", {
      edify_user_id: userData?.id || "",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "work_bag",
      action: "apply_to_offer",
      offer_id: workOffer?.id || "",
    });
    if (isExternalOffer) {
      setShowApplyExternalJobOffer(true);
    } else {
      setShowApplyModal(false);
      navigate(`/bolsadetrabajo/${workOffer?.id}`);
    }
  };

  const handleClose = () => setShowApplyModal(false);

  const needsProfileCompletion = userNeedsJobBoardProfileCompletion(userData);

  if (needsProfileCompletion) {
    return (
      <ModalOverlay isOpen={true} onClose={forbiddenClose ? () => {} : handleClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <header className={styles.header}>
            <h2 className={styles.title}>Completá tu perfil</h2>
            {!forbiddenClose && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            )}
          </header>
          <div className={styles.body}>
            <p className={styles.profileGateLead}>
              Para ver el detalle de la oferta y postularte necesitás completar los datos de tu perfil
              de bolsa de trabajo.
            </p>
            <button
              type="button"
              className={styles.profileGatePrimary}
              onClick={() => {
                setShowApplyModal(false);
                navigate("/bolsadetrabajo/completar-perfil", {
                  state: { fromApply: true, offerId: workOffer?.id },
                });
              }}
            >
              Ir a completar perfil
            </button>
          </div>
        </div>
      </ModalOverlay>
    );
  }

  if (showApplyExternalJobOffer) {
    return (
      <ApplyExternalJobOffer
        workOffer={workOffer}
        isOpen={showApplyExternalJobOffer}
        onClose={() => {
          setShowApplyExternalJobOffer(false);
          setShowApplyModal(false);
        }}
      />
    );
  }

  return (
    <ModalOverlay isOpen={true} onClose={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Postularte a esta oferta</h2>
          {isExternalOffer && !forbiddenClose && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>
          )}
        </header>

        <div className={styles.body}>
          {(registrationStep === "email" || registrationStep === "existing-login") && (
            <>
              {isExternalOffer && (
                <div className={styles.externalBadge}>
                  <ExternalLink size={16} />
                  <span>Búsqueda externa: te acercamos el contacto, la contratación la gestiona quien publicó.</span>
                </div>
              )}
              {hasOfferContext && (
            <div className={styles.offerContext}>
              <p className={styles.offerContextLabel}>
                {isExternalOffer ? "Detalles de la búsqueda" : "Estás por postularte a:"}
              </p>
              <div className={styles.offerContextCard}>
                {!isExternalOffer && companyLabel && (
                  <div className={styles.offerContextRow}>
                    <Briefcase size={16} className={styles.offerContextIcon} />
                    <span className={styles.offerContextValue}>{companyLabel}</span>
                  </div>
                )}
                {skillNames.length > 0 && (
                  <div className={styles.offerContextRow}>
                    <span className={styles.offerContextChips}>
                      {skillNames.slice(0, 3).join(" · ")}
                    </span>
                  </div>
                )}
                {(primaryZone || salaryLabel !== "A convenir" || activeUntilLabel) && (
                  <div className={styles.offerContextMeta}>
                    {primaryZone && (
                      <span className={styles.offerContextMetaItem}>
                        <MapPin size={14} />
                        {primaryZone}
                      </span>
                    )}
                    {salaryLabel !== "A convenir" && (
                      <span className={styles.offerContextMetaItem}>
                        <DollarSign size={14} />
                        {salaryLabel}
                      </span>
                    )}
                    {activeUntilLabel && (
                      <span className={styles.offerContextMetaItem}>
                        <Clock size={14} />
                        Postulá hasta {activeUntilLabel}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
            </>
          )}

          <JobboardRegistrationFlow
            embedded
            onClose={handleClose}
            workOffer={workOffer}
            isExternalOffer={isExternalOffer}
            registrationEntry="apply"
            onSuccess={handleRegistrationSuccess}
            onStepChange={setRegistrationStep}
          />
        </div>
      </div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state: { general?: { userData?: WorkApplyModalProps["userData"] } }) => ({
  userData: state.general?.userData,
});

export default connect(mapStateToProps)(WorkApplyModal);
